import React, { useCallback, useEffect, useRef, useState } from "react"
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  getDOMSelection,
  SELECTION_CHANGE_COMMAND,
  type BaseSelection,
  type LexicalCommand,
  type RangeSelection,
} from "lexical"
import { createPortal } from "react-dom"

import mergeRegister, {
  getDOMRangeRect,
  getSelectedNode,
  setFloatingElemPosition,
} from "@/components/lexical/plugins/floating-text-format-toolbar/utils"

export const OPEN_FLOATING_LINK_EDITOR_COMMAND: LexicalCommand<void> = createCommand(
  "OPEN_FLOATING_LINK_EDITOR_COMMAND"
)

export const validateLinkUrl = (url: string): boolean => /^https?:\/\//i.test(url.trim())

const normalizeUrl = (url: string): string => {
  const trimmed = url.trim()
  if (!trimmed) return ""
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed
  }
  return `https://${trimmed}`
}

const getSelectedLinkNode = (selection: RangeSelection) => {
  const node = getSelectedNode(selection)
  const parent = node.getParent()
  const grandParent = parent?.getParent()

  if ($isLinkNode(node)) return node
  if ($isLinkNode(parent)) return parent
  if ($isLinkNode(grandParent)) return grandParent

  return null
}

interface FloatingLinkEditorProps {
  anchorElem: HTMLElement
}

export default function FloatingLinkEditorPlugin({ anchorElem }: FloatingLinkEditorProps): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  const editorRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const lastSelectionRef = useRef<RangeSelection | null>(null)
  const openViaCommandRef = useRef(false)

  const [isVisible, setIsVisible] = useState(false)
  const isVisibleRef = useRef(isVisible)
  const [linkUrl, setLinkUrl] = useState("")
  const [showValidationError, setShowValidationError] = useState(false)
  const [isLinkNodeSelected, setIsLinkNodeSelected] = useState(false)

  const hideEditor = useCallback(() => {
    setIsVisible(false)
    setShowValidationError(false)
    requestAnimationFrame(() => {
      editor.focus()
    })
  }, [editor])

  useEffect(() => {
    isVisibleRef.current = isVisible
    if (!isVisible) {
      const elem = editorRef.current
      if (elem) {
        setFloatingElemPosition(null, elem, anchorElem)
      }
    }
  }, [anchorElem, isVisible])

  const updatePosition = useCallback(() => {
    const elem = editorRef.current
    if (!elem) return

    editor.getEditorState().read(() => {
      const selection = $getSelection()
      const nativeSelection = getDOMSelection(editor._window)
      const rootElement = editor.getRootElement()

      if (!selection || !nativeSelection || !rootElement || !rootElement.contains(nativeSelection.anchorNode)) {
        setFloatingElemPosition(null, elem, anchorElem)
        return
      }

      const rangeRect = getDOMRangeRect(nativeSelection, rootElement)
      setFloatingElemPosition(rangeRect, elem, anchorElem, 8)
    })
  }, [anchorElem, editor])

  const updateFromSelection = useCallback(
    (selection: BaseSelection | null) => {
      const elem = editorRef.current

      if (!selection || !$isRangeSelection(selection)) {
        lastSelectionRef.current = null
        setIsLinkNodeSelected(false)

        if (!isVisibleRef.current) {
          if (elem) setFloatingElemPosition(null, elem, anchorElem)
        }
        return
      }

      lastSelectionRef.current = selection.clone()

      const linkNode = getSelectedLinkNode(selection)
      const nextUrl = linkNode ? linkNode.getURL() : ""

      setIsLinkNodeSelected(!!linkNode)

      if (!isVisibleRef.current) {
        setLinkUrl(nextUrl)
      }

      if (isVisibleRef.current) {
        queueMicrotask(() => updatePosition())
      } else if (linkNode && elem) {
        // Keep the floating UI aligned with selection to reduce jump when opening
        queueMicrotask(() => updatePosition())
      }
    },
    [anchorElem, updatePosition]
  )

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateFromSelection($getSelection())
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateFromSelection($getSelection())
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        OPEN_FLOATING_LINK_EDITOR_COMMAND,
        () => {
          let shouldOpen = false

          editor.getEditorState().read(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) {
              return
            }

            const hasTextSelection = !selection.isCollapsed() && selection.getTextContent().trim().length > 0
            const node = getSelectedNode(selection)
            const parent = node.getParent()

            if (hasTextSelection || $isLinkNode(node) || ($isLinkNode(parent) && selection.isCollapsed())) {
              shouldOpen = true
            }
          })

          if (shouldOpen) {
            openViaCommandRef.current = true
            setIsVisible(true)
          }

          return true
        },
        COMMAND_PRIORITY_LOW
      )
    )
  }, [editor, updateFromSelection])

  useEffect(() => {
    if (!isVisible) return

    updatePosition()

    const scroller = anchorElem.parentElement
    const handleScroll = () => updatePosition()
    const handleResize = () => updatePosition()

    scroller?.addEventListener("scroll", handleScroll)
    window.addEventListener("resize", handleResize)

    queueMicrotask(() => {
      inputRef.current?.focus()
      if (openViaCommandRef.current) {
        openViaCommandRef.current = false
        inputRef.current?.select()
      }
    })

    return () => {
      scroller?.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [anchorElem, isVisible, updatePosition])

  const applyLink = useCallback(() => {
    if (!lastSelectionRef.current) {
      hideEditor()
      return
    }

    const normalized = normalizeUrl(linkUrl)

    if (!normalized || !validateLinkUrl(normalized)) {
      setShowValidationError(true)
      return
    }

    editor.update(() => {
      const selection = lastSelectionRef.current
      if (!selection) return
      $setSelection(selection.clone())
      const currentSelection = $getSelection()

      if ($isRangeSelection(currentSelection)) {
        const linkNode = getSelectedLinkNode(currentSelection)
        if (linkNode) {
          linkNode.setURL(normalized)
          return
        }
      }

      editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalized)
    })

    hideEditor()
  }, [editor, hideEditor, linkUrl])

  const removeLink = useCallback(() => {
    if (!lastSelectionRef.current) {
      hideEditor()
      return
    }

    editor.update(() => {
      const selection = lastSelectionRef.current
      if (!selection) return
      $setSelection(selection.clone())
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    })

    hideEditor()
  }, [editor, hideEditor])

  const onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      applyLink()
    },
    [applyLink]
  )

  const onInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        event.preventDefault()
        hideEditor()
      }
    },
    [hideEditor]
  )

  if (!isVisible) {
    return null
  }

  return createPortal(
    <div
      ref={editorRef}
      className="border-border-accent bg-background absolute top-0 left-0 z-50 w-80 rounded-md border p-2 shadow-lg"
    >
      <form className="flex w-full flex-col gap-2" onSubmit={onSubmit}>
        <div className="flex flex-col gap-1">
          <label className="text-foreground text-xs font-medium" htmlFor="lexical-link-input">
            Link URL
          </label>
          <input
            ref={inputRef}
            id="lexical-link-input"
            className={`bg-background text-foreground focus-visible:ring-ring rounded-sm px-2 py-1 text-sm outline-none focus-visible:ring-2 ${
              showValidationError ? "ring-destructive ring-2" : "ring-border/50 ring-1"
            }`}
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(event) => {
              setLinkUrl(event.target.value)
              if (showValidationError) {
                setShowValidationError(false)
              }
            }}
            onKeyDown={onInputKeyDown}
          />
          {showValidationError && (
            <span className="text-destructive text-xs">Enter a valid URL that starts with http:// or https://</span>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          {isLinkNodeSelected && (
            <button type="button" className="text-muted-foreground hover:text-destructive text-xs" onClick={removeLink}>
              Remove link
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button type="button" className="text-muted-foreground text-xs" onClick={hideEditor}>
              Cancel
            </button>
            <button
              type="submit"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm px-3 py-1 text-xs font-medium"
            >
              Apply
            </button>
          </div>
        </div>
      </form>
    </div>,
    anchorElem
  )
}
